import _ from 'lodash';
import Combinatorics from 'js-combinatorics';

import { SOCKET_TYPES, FAKE_INSTANCE_IDS, FAKE_ENCOUNTER_IDS } from 'shared/constants';
import {
  CRAFTED_STAT_COMBOS,
  numSockets,
  getBonusIdForSocketNum,
  canAddSocket,
  craftedStatsOrderMatters,
  doesItemDrop,
  getBonusIds,
  getBonusUpgradeSet,
  getBonusVariations,
  getCraftedStats,
  getIndexForSequenceLevel,
  getItemBonusForDelta,
  getItemSocketBonusId,
  getMaxUpgradeInfo,
  getUpgradeSetLevel,
  getPossibleItemConversions,
  getSeasonBonusForLevel,
  getUpgradeInfoForBonus,
  getUpgradeBonusForLevel,
  getWeaponHands,
  hasCraftedStats,
  numCraftedStats,
  isBaseLevelBonus,
  isItemLevelBonus,
  isItemUsable,
  isItemVariationBonus,
  isSocketBonus,
  isUngatedSocketSlot,
  itemSocketInfo,
  professionCraftingQualities,
  removeAllPrismaticSockets,
  removeItemLevelBonuses,
  setItemNumSockets,
  slotMap,
} from 'shared/items';
import { getGemDetails } from 'shared/gems';
import { getSpecInfo } from 'shared/character';
import { addTag } from 'shared/itemTags';
import { DEVMODE } from 'shared/util';

import GameData from 'shared/gameData';

let loaded = false;

let Instances;
let Encounters;
let EncounterItems;

const getInstance = instanceId => _.find(Instances, { id: instanceId });
const getEncounter = encounterId => _.find(Encounters, { id: encounterId });

const loadStaticData = data => {
  if (!loaded) {
    EncounterItems = data.EncounterItems;
    Instances = data.Instances;

    Encounters = _.flatten(_.map(Instances, 'encounters'));
    loaded = true;
  }
};

const DEBUG_ITEM_ID = null;

const DEFAULT_VERY_RARE_SEQUENCE_OFFSET = 2;

const WARFORGED_BONUS_ID = 4783;
const TITANFORGED_BONUS_ID = 4784;

const jewelryIgnoreInstances = [
  1178, // Mechagon - ignores the Big Band rings
];

const mythicPlusItemFilter = (item) => {
  const slot = slotMap[item.inventoryType];
  if (slot === 'neck' || slot === 'finger') {
    const ignoredInstance = _.some(item.sources, s => _.includes(jewelryIgnoreInstances, s.instanceId));
    return !ignoredInstance;
  }
  const realSources = _.filter(item.sources, s => s.instanceId > 0);
  const mplusSources = _.filter(realSources, source => {
    const encounter = getEncounter(source.encounterId);
    return !encounter.difficulties || _.includes(encounter.difficulties, 8);
  });

  // remove any items that come from encounters that aren't in M+
  // This can happen with M+ instances that are part of a megadungeon split.
  // In 11.1, Mechagon Workshop is in the season but the first half is not. This remove the earlier items
  if (_.size(mplusSources) === 0) {
    return false;
  }

  return true;
};


const MPLUS_ITEM_LEVELS = [{
  id: 'dungeon-heroic',
  name: 'Heroic',
  quality: 3, // rare
  itemLevelOverride: 619,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    7244, // rare
  ],
}, {
  id: 'dungeon-mythic1',
  name: 'Mythic',
  itemLevelOverride: 636,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic2',
  name: 'Mythic 2',
  itemLevelOverride: 639,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic3',
  name: 'Mythic 3',
  itemLevelOverride: 639,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic4',
  name: 'Mythic 4',
  itemLevelOverride: 642,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic5',
  name: 'Mythic 5',
  itemLevelOverride: 645,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic6',
  name: 'Mythic 6',
  itemLevelOverride: 649,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic7',
  name: 'Mythic 7',
  itemLevelOverride: 649,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic8',
  name: 'Mythic 8',
  itemLevelOverride: 652,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic9',
  name: 'Mythic 9',
  itemLevelOverride: 652,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}, {
  id: 'dungeon-mythic10',
  name: 'Mythic 10',
  itemLevelOverride: 655,
  itemFilter: mythicPlusItemFilter,
  bonusIds: [
    4786, // epic
  ],
}];

const MPLUS_DIFFICULTIES = [
  // ======================================================
  // Merge in mlpus levels into mplus-chest type
  // We also reuse these item levels for M+ timewalking
  ..._.map(MPLUS_ITEM_LEVELS, itemLevelInfo => ({
    type: 'mplus-chest',
    ...itemLevelInfo,
  })),
  {
    id: 'dungeon-mythic-weekly7',
    type: 'mplus-chest',
    name: '+7-9 Vault',
    itemLevelOverride: 658,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      4786, // epic
    ],
  }, {
    id: 'dungeon-mythic-weekly10',
    type: 'mplus-chest',
    name: '+10 Vault',
    itemLevelOverride: 662,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      4786, // epic
    ],
  },
];

/*
const TIMEWALKING_DIFFICULTIES = [
  ..._.map(MPLUS_ITEM_LEVELS, itemLevelInfo => ({
    type: 'mplus-timewalking',
    ...itemLevelInfo,
  })), {
    id: 'dungeon-upgrade-10',
    type: 'mplus-timewalking',
    name: 'Upgrade Level 10',
    itemLevelOverride: 291,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      7604, // Upgrade Level 10
      4786, // epic
    ],
  }, {
    id: 'dungeon-upgrade-11',
    type: 'mplus-timewalking',
    name: 'Upgrade Level 11',
    itemLevelOverride: 294,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      7605, // Upgrade Level 11
      4786, // epic
    ],
  }, {
    id: 'dungeon-upgrade-12',
    type: 'mplus-timewalking',
    name: 'Upgrade Level 12',
    itemLevelOverride: 298,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      7606, // Upgrade Level 12
      4786, // epic
    ],
  },
];
*/

const professionLevelUpgrades = [{
  key: 'base',
  name: 'Base',
  archive: true,
}, {
  key: 'base545',
  name: 'Soul Sigil I',
  baseLevel: 545,
  itemId: 228338,
  itemQuality: 3,
  archive: true,
}, {
  key: 'base561',
  name: 'Soul Sigil II',
  baseLevel: 561,
  itemId: 228339,
  itemQuality: 3,
  archive: true,
}, {
  key: 'base577',
  name: 'Enchanted Weathered Harbinger Crest',
  baseLevel: 577,
  itemId: 224069,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base606',
  name: 'Enchanted Runed Harbinger Crest',
  baseLevel: 606,
  itemId: 224072,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base623',
  name: 'Enchanted Gilded Harbinger Crest',
  baseLevel: 623,
  itemId: 224073,
  itemQuality: 4,
  archive: true,
}, {

  // Season 2

  key: 'base616',
  name: 'Enchanted Weathered Undermine Crest',
  baseLevel: 616,
  itemId: 230937,
  itemQuality: 4,
}, {
  key: 'base632',
  name: 'Spark of Fortunes',
  baseLevel: 632,
  itemId: 230906,
  itemQuality: 4,
}, {
  key: 'base645',
  name: 'Enchanted Runed Undermine Crest',
  baseLevel: 645,
  itemId: 230936,
  itemQuality: 4,
}, {
  key: 'base662',
  name: 'Enchanted Gilded Undermine Crest',
  baseLevel: 662,
  itemId: 230935,
  itemQuality: 4,
  /*
}, {
  key: 'base408',
  name: 'Concentrated Primal Infusion',
  baseLevel: 408,
  itemId: 198046,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base411',
  name: 'Spark of Shadowflame',
  baseLevel: 411,
  itemId: 204440,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base424',
  name: 'Enchanted Wyrm\'s Shadowflame Crest',
  baseLevel: 424,
  itemId: 204682,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base434',
  name: 'Enchanted Aspect\'s Shadowflame Crest',
  baseLevel: 434,
  itemId: 204697,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base450',
  name: 'Spark of Dreams',
  baseLevel: 450,
  itemId: 206959,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base463',
  name: 'Enchanted Wyrm\'s Dreaming Crest',
  baseLevel: 463,
  itemId: 206960,
  itemQuality: 4,
  archive: true,
}, {
  key: 'base473 ',
  name: 'Enchanted Aspect\'s Dreaming Crest',
  baseLevel: 473,
  itemId: 206961,
  itemQuality: 4,
  archive: true,
}, {

  // Season 4

  key: 'base489',
  name: 'Spark of Awakening',
  baseLevel: 489,
  itemId: 211516,
  itemQuality: 4,
}, {
  key: 'base502',
  name: 'Enchanted Wyrm\'s Awakened Crest',
  baseLevel: 502,
  itemId: 211518,
  itemQuality: 4,
}, {
  key: 'base512 ',
  name: 'Enchanted Aspect\'s Awakened Crest',
  baseLevel: 512,
  itemId: 211519,
  itemQuality: 4,
  */
}];

// This can get a bit weird if there's a "jagged" item level band of early items
// DF launch has some 306 items that are obnoxious
const professionDifficulties = ({ baseLevel, maxLevel = Infinity, excludeUpgrades = [] }) => _.filter(_.map(
  Combinatorics.cartesianProduct(professionCraftingQualities, professionLevelUpgrades).toArray(),
  ([craftingQuality, levelUpgrade]) => {
    if (_.includes(excludeUpgrades, levelUpgrade.key)) {
      return null;
    }
    const base = levelUpgrade.baseLevel || baseLevel;
    const level = base + craftingQuality.levelOffset;
    if (!levelUpgrade.baseLevel || (baseLevel < levelUpgrade.baseLevel && level <= maxLevel)) {
      return {
        id: `profession${baseLevel}-${level}-${_.camelCase(levelUpgrade.name)}`,
        type: `profession${baseLevel}`,
        name: `${levelUpgrade.name}, Quality ${craftingQuality.tier}`,
        itemLevelOverride: level,
        craftingQuality: craftingQuality.tier,
        archive: levelUpgrade.archive,

        itemId: levelUpgrade.itemId,
        itemQuality: levelUpgrade.itemQuality,
      };
    }
    return null;
  },
));

const PROFESSION_DIFFICULTIES = [
  ...professionDifficulties({
    baseLevel: 515,
    maxLevel: 629,
  }),
  ...professionDifficulties({
    baseLevel: 593,
    excludeUpgrades: ['base616'],
  }),
];

/*
const isNewPrimalStormItem = item => {
  // remove the 10.0.5 items from the low level group
  const slot = slotMap[item.inventoryType];
  return _.includes(['neck', 'finger', 'trinket'], slot);
};

const isTrinket = item => slotMap[item.inventoryType] === 'trinket';
*/

export const DIFFICULTIES = [
  ...MPLUS_DIFFICULTIES,

  //...TIMEWALKING_DIFFICULTIES,

  {
    id: 'dungeon-normal',
    type: 'expansion-dungeon',
    name: 'Normal',
    quality: 3, // rare
    itemLevelOverride: 593,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      7244, // rare
    ],
  }, {
    id: 'dungeon-heroic',
    type: 'expansion-dungeon',
    name: 'Heroic',
    quality: 3, // rare
    itemLevelOverride: 619,
    itemFilter: mythicPlusItemFilter,
    bonusIds: [
      7244, // rare
    ],
  }, {
    id: 'raid-lfr',
    type: 'raid',
    name: 'Raid Finder',
    bonusIds: [
      4825, // Raid Finder
      4786, // epic
    ],
  }, {
    id: 'raid-normal',
    type: 'raid',
    name: 'Normal',
    bonusIds: [
      4822, // Normal (doesn't actually do anything)
      4786, // epic
    ],
  }, {
    id: 'raid-heroic',
    type: 'raid',
    name: 'Heroic',
    bonusIds: [
      4799, // Heroic
      4786, // epic
    ],
  }, {
    id: 'raid-mythic',
    type: 'raid',
    name: 'Mythic',
    bonusIds: [
      4800, // Mythic
      4786, // epic
    ],

    /*
  }, {
    id: 'raid-lfr-fated',
    type: 'raid',
    name: 'Awakened Raid Finder',
    context: 83,
    bonusIds: [
      10884, // Awakened
      4786, // epic
    ],
    itemLevel: '480 - 489',

    levelSelectorSequence: 355,
  }, {
    id: 'raid-normal-fated',
    type: 'raid',
    name: 'Awakened Normal',
    context: 82,
    bonusIds: [
      10884, // Awakened
      4786, // epic
    ],
    itemLevel: '493 - 502',

    levelSelectorSequence: 356,
  }, {
    id: 'raid-heroic-fated',
    type: 'raid',
    name: 'Awakened Heroic',
    context: 84,
    bonusIds: [
      10884, // Awakened
      4786, // epic
    ],
    itemLevel: '506 - 515',

    levelSelectorSequence: 357,
  }, {
    id: 'raid-mythic-fated',
    type: 'raid',
    name: 'Awakened Mythic',
    context: 85,
    bonusIds: [
      10884, // Awakened
      4786, // epic
    ],
    itemLevel: '519 - 528',

    levelSelectorSequence: 358,
  */

  }, {

    // PVP VENDORS

    id: 'pvp-honor',
    type: 'pvp-honor',
    name: 'Base Level',
    quality: 2,
    itemLevelOverride: 597,

  }, {
    id: 'pvp-world-rare',
    type: 'pvp-world',
    name: 'Bloody Tokens',
    quality: 4,
    itemLevelOverride: 632,
    /*
  }, {
    id: 'pvp-world-epic',
    type: 'pvp-world',
    name: 'Trophy of Strife',
    itemLevelOverride: 593,
    bonusIds: [
      4786, // epic
    ],
    */
  }, {
    id: 'pvp-conquest',
    type: 'pvp-conquest',
    name: 'Base Level',
    itemLevelOverride: 636,

  }, {
    id: 'megadungeon-heroic',
    type: 'megadungeon',
    name: 'Heroic',
    itemLevelOverride: 476,
  }, {
    id: 'megadungeon-mythic',
    type: 'megadungeon',
    name: 'Mythic',
    itemLevelOverride: 493,
  }, {
    id: 'megadungeon-mythic-hard',
    type: 'megadungeon',
    name: 'Mythic Hard Mode',
    itemLevelOverride: 506,
    itemLevel: '506',
  },

  ..._.reject(_.map(_.uniqBy(MPLUS_DIFFICULTIES, 'itemLevelOverride'), itemLevelInfo => ({
    id: `catalyst-${itemLevelInfo.itemLevelOverride}`,
    type: 'catalyst',
    itemLevelOverride: itemLevelInfo.itemLevelOverride,
  })), (diff) => !diff.itemLevelOverride),

  ...PROFESSION_DIFFICULTIES,

  // Delves
  /*
  {
    id: 'delve-rare',
    type: 'delve-rare',
    name: 'Vendor',
    itemLevelOverride: 567,
    quality: 2,
  }, {
  */

  {
    id: 'delve-epic-1',
    type: 'delve-epic',
    name: 'Tier 1',
    itemLevelOverride: 610,
    quality: 4,
  }, {
    id: 'delve-epic-2',
    type: 'delve-epic',
    name: 'Tier 2',
    itemLevelOverride: 613,
    quality: 4,
  }, {
    id: 'delve-epic-3',
    type: 'delve-epic',
    name: 'Tier 3',
    itemLevelOverride: 616,
    quality: 4,
  }, {
    id: 'delve-epic-4',
    type: 'delve-epic',
    name: 'Tier 4',
    itemLevelOverride: 619,
    quality: 4,
  }, {
    id: 'delve-epic-5',
    type: 'delve-epic',
    name: 'Tier 5',
    itemLevelOverride: 623,
    quality: 4,
  }, {
    id: 'delve-epic-6',
    type: 'delve-epic',
    name: 'Tier 6',
    itemLevelOverride: 626,
    quality: 4,
  }, {
    id: 'delve-epic-7',
    type: 'delve-epic',
    name: 'Tier 7',
    itemLevelOverride: 636,
    quality: 4,
  }, {
    id: 'delve-epic-8',
    type: 'delve-epic',
    name: 'Tier 8 - 11',
    itemLevelOverride: 639,
    quality: 4,
  }, {
    id: 'delve-epic-vault5',
    type: 'delve-epic',
    name: 'Vault - Tier 5',
    itemLevelOverride: 642,
    quality: 4,
  }, {
    id: 'delve-epic-vault6',
    type: 'delve-epic',
    name: 'Vault - Tier 6',
    itemLevelOverride: 645,
    quality: 4,
  }, {
    id: 'delve-epic-vault8',
    type: 'delve-epic',
    name: 'Vault - Tier 8 - 11',
    itemLevelOverride: 649,
    quality: 4,
  },

  /*
  {
    id: 'primalStorms-rare',
    type: 'primalStorms',
    name: 'Level 1',
    itemLevelOverride: 359,
    quality: 3,
    itemFilter: (item) => !isNewPrimalStormItem(item),
  }, {
    id: 'primalStorms-epic',
    type: 'primalStorms',
    name: 'Level 2',
    quality: 4,
    itemLevelOverride: 385,
    bonusIds: [
      8943, // "Primal Infused" tag
    ],
    itemFilter: (item) => !isNewPrimalStormItem(item),
  }, {
    id: 'primalStorms-389',
    type: 'primalStorms',
    name: 'Rings, Neck, Trinket',
    quality: 4,
    itemLevelOverride: 389,
    bonusIds: [
      8943, // "Primal Infused" tag
    ],
    itemFilter: (item) => isNewPrimalStormItem(item),
  }, {
    id: 'primalStorms-3',
    type: 'primalStorms',
    name: 'Level 3',
    quality: 4,
    itemLevelOverride: 395,
    bonusIds: [
      // this is a guess, seems logical
      9378, // "Primal Empowered" tag
    ],
    itemFilter: (item) => !isNewPrimalStormItem(item),
  }, {

    id: 'timeRifts-flakes',
    type: 'solo',
    name: 'Trinkets',
    quality: 4,
    itemLevelOverride: 454,
    itemFilter: isTrinket,
  }, {
    id: 'timeRifts',
    type: 'solo',
    name: 'Weapons/Armor',
    quality: 4,
    itemLevelOverride: 467,
    itemFilter: item => !isTrinket(item),
  }, {

    // dreamsurge 402

    id: 'dreamsurges-rare',
    type: 'dreamsurges',
    name: 'Dreamsurges',
    quality: 3,
    itemLevelOverride: 454,
  }, {

    // dreamsurge 415
    id: 'dreamsurges-epic',
    type: 'dreamsurges',
    name: 'Dreamsurges',
    quality: 4,
    itemLevelOverride: 467,
  }, {

    // DF Season 4 Vendor

    id: 'raid-vendor-season4',
    type: 'raid-vendor-season4',
    name: 'Raid Vendor Season 4',
    quality: 4,
    itemLevelOverride: 493,
  },
  */
];

export const INSTANCE_OVERRIDES = [{

  /////////////////////////////////////////////////////////////////////////////////
  //
  // 11.1
  //
  /////////////////////////////////////////////////////////////////////////////////


  // 11.1 Liberation of Undermine
  instanceId: 1296,
  season: 'tww2',
  levelSelectorSetUpgradeTrack: true,

  expandCraftedStats: true,

}, {

  // 11.1 has a Mechagon wing in M+. This hides the full Mechagon from the Droptimizer list
  instanceId: 1178,
  disabled: true,

}, {

  // MYTHIC+ END OF DUNGEON CHEST
  instanceId: -1,
  season: 'tww2',

  showEncounters: true,

  encounterType: 'dungeon',
  encounterTypePlural: 'dungeons',
  encounterAllId: -1,

  enableSockets: true,
  repeatable: true,

}, {

  // Normal/Heroic/M0 Dungeons
  instanceId: -32,
  season: 'tww2',

  showEncounters: true,

  encounterType: 'dungeon',
  encounterTypePlural: 'dungeons',
  encounterAllId: -32,

  enableSockets: true,
  repeatable: true,
}, {
  instanceId: FAKE_INSTANCE_IDS.delveEpicsWarWithinSeason1,
  season: 'tww2',

  warning: {
    text: 'Delves loot tables are not fully known at this time',
  },
}, {

  // Season 2 PVP Conquest
  instanceId: FAKE_INSTANCE_IDS.pvpConquestWarWithinSeason2,
  season: 'tww2',

  showChances: false,
  disableDifficulties: true,
}, {
  // Season 2 PVP Honor
  instanceId: FAKE_INSTANCE_IDS.pvpHonorWarWithinSeason2,
  season: 'tww2',

  showChances: false,
  disableDifficulties: true,
}, {
  // Season 2 PVP World
  instanceId: FAKE_INSTANCE_IDS.pvpWorldWarWithinSeason2,
  season: 'tww2',

  showChances: false,
  disableDifficulties: true,
}, {

  /////////////////////////////////////////////////////////////////////////////////
  //
  // 11.0.5
  //
  /////////////////////////////////////////////////////////////////////////////////

  // 11.0.5 Blackrock Depths
  instanceId: 1301,
  season: 'tww1',
  levelSelectorSetUpgradeTrack: true,

  archive: true,

}, {

  /////////////////////////////////////////////////////////////////////////////////
  //
  // THE WAR WITHIN RELEASE 11.0.2
  //
  /////////////////////////////////////////////////////////////////////////////////

  // 11.0 CATALYST - tier upgrader
  instanceId: FAKE_INSTANCE_IDS.catalystWarWithinSeason1,
  season: 'tww1',

  archive: true,

  disableWarforgeLevel: true,

  enableSockets: true,

  showChances: false,
}, {

  // TWW WORLD BOSSES
  instanceId: 1278,
  season: 'tww1',
  disableDifficulties: true,

  itemLevelOverride: 603,

  enableSockets: true,

}, {

  // 11.0 Nerub-ar Palace
  instanceId: 1273,
  season: 'tww1',
  levelSelectorSetUpgradeTrack: true,

  archive: true,

}, {

  // PROFESSIONS Epic items
  instanceId: FAKE_INSTANCE_IDS.warWithinProfessionsEpic,
  disableDifficulties: false,

  showEncounters: false,

  encounterType: 'profession',
  encounterTypePlural: 'professions',
  encounterAllId: FAKE_INSTANCE_IDS.warWithinProfessionsEpic,

  showChances: false,

  enableSockets: true,

  // NOT seasonal gear
  season: '',
}, {
  // PROFESSIONS rare items
  instanceId: FAKE_INSTANCE_IDS.warWithinProfessionsRare,
  disableDifficulties: false,

  showEncounters: false,

  encounterType: 'profession',
  encounterTypePlural: 'professions',
  encounterAllId: FAKE_INSTANCE_IDS.warWithinProfessionsRare,

  showChances: false,

  enableSockets: true,

  // NOT seasonal gear
  season: '',

}, {

  // Season 1 PVP Conquest
  instanceId: FAKE_INSTANCE_IDS.pvpConquestWarWithinSeason1,
  season: 'tww1',

  showChances: false,
  disableDifficulties: true,

  archive: true,
}, {
  // Season 1 PVP Honor
  instanceId: FAKE_INSTANCE_IDS.pvpHonorWarWithinSeason1,
  season: 'tww1',

  showChances: false,
  disableDifficulties: true,

  archive: true,
}, {
  // Season 1 PVP World
  instanceId: FAKE_INSTANCE_IDS.pvpWorldWarWithinSeason1,
  season: 'tww1',

  showChances: false,
  disableDifficulties: true,

  archive: true,
}, {

  /////////////////////////////////////////////////////////////////////////////////
  //
  // Dragonflight
  //
  /////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////
  // Dragonflight Season 4
  //////////////////////////////////////

  // 10.2 CATALYST - tier upgrader
  instanceId: -46,
  season: 'df4',

  disableWarforgeLevel: true,

  enableSockets: true,

  showChances: false,
}, {
  // Season 4 PVP Conquest
  instanceId: -60,
  season: 'df4',

  showChances: false,
  disableDifficulties: true,
}, {
  // Season 4 PVP Honor
  instanceId: -59,
  season: 'df4',

  showChances: false,
  disableDifficulties: true,
}, {
  // Season 4 PVP World
  instanceId: -61,
  season: 'df4',

  showChances: false,
}, {

  //////////////////////////////////////
  // Dragonflight Season 3
  //////////////////////////////////////

  // 10.2 Amirdrassil
  instanceId: 1207,
  season: 'df4',

  archive: true,

}, {
  // 10.2 PVP Conquest
  instanceId: -53,
  season: 'df3',

  showChances: false,
  disableDifficulties: true,

  archive: true,
}, {
  // 10.2 PVP Honor
  instanceId: -52,
  season: 'df3',

  showChances: false,
  disableDifficulties: true,

  archive: true,
}, {
  // 10.2 PVP World
  instanceId: -57,
  season: 'df3',

  showChances: false,

  archive: true,
}, {

  // Dreamsurges have been bumped to season 4
  instanceId: -51,
  season: 'df4',

}, {

  // Time Rifts have been bumped to season 4
  instanceId: -50,
  season: 'df4',

}, {

  //////////////////////////////////////
  // Dragonflight Season 2
  //////////////////////////////////////


  // Dawn of the Infinite was originally season 2. Items are now season 3 track
  instanceId: 1209,
  season: 'df4',
}, {

  // 10.1 Aberrus
  instanceId: 1208,
  season: 'df4',
  archive: false,

}, {

  // 10.1 PVP Conquest
  instanceId: -47,
  season: 'df2',

  showChances: false,

  archive: true,
}, {
  // 10.1 PVP Honor
  instanceId: -48,
  season: 'df2',

  showChances: false,

  archive: true,
}, {

  //////////////////////////////////////
  // Dragonflight Season 1
  //////////////////////////////////////

  // 10.0 PVP Conquest
  instanceId: -31,
  season: 'df1',

  showChances: false,

  archive: true,
}, {
  // 10.0 PVP Honor
  instanceId: -30,
  season: 'df1',

  showChances: false,

  archive: true,
}, {
  // 10.0 Vault of the Incarnates raid
  instanceId: 1200,
  season: 'df4',

  archive: false,
}, {
  // 10.0 PRIMAL STORMS
  instanceId: -45,
  season: 'df1',

  showEncounters: false,

  enableSockets: false,

  showChances: false,

  archive: true,
}, {

  // PROFESSIONS 382 items
  instanceId: -42,
  disableDifficulties: false,

  showEncounters: false,

  encounterType: 'profession',
  encounterTypePlural: 'professions',
  encounterAllId: -42,

  showChances: false,

  enableSockets: true,

  // NOT seasonal gear
  season: '',
}, {
  // PROFESSIONS 333 items
  instanceId: -41,
  disableDifficulties: false,

  showEncounters: false,

  encounterType: 'profession',
  encounterTypePlural: 'professions',
  encounterAllId: -41,

  showChances: false,

  enableSockets: true,

  warning: {
    text: '306 items and Titan Training Matrix I infusions are not supported',
  },

  // NOT seasonal gear
  season: '',

  archive: true,
}, {
  // DF WORLD BOSSES
  instanceId: 1205,
  season: 'df3',
  disableDifficulties: true,

  itemLevelOverride: 395,

  enableSockets: true,
}];

export const SEASONS = {
  tww2: {
    seasonId: 25,

    disableWarforgeLevel: true,
    enableSockets: true,

    itemConversion: {
      id: 10,
      minLevel: 623, // TODO: This is a guess based on lowest level of LFR drops
    },
  },
  tww1: {
    seasonId: 24,

    disableWarforgeLevel: true,
    enableSockets: true,

    itemConversion: {
      id: 8,
      minLevel: 584, // TODO: This is a guess based on lowest level of LFR drops
    },
  },
  df4: {
    seasonId: 23,

    disableWarforgeLevel: true,
    enableSockets: true,

    // Dragonflight season 4 Catalyst conversion
    itemConversion: {
      id: 9,
      minLevel: 480, // TODO: This is a guess based on lowest level of LFR drops
    },
  },
  df3: {
    seasonId: 22,

    disableWarforgeLevel: true,
    enableSockets: true,

    // Dragonflight season 3 Catalyst conversion
    itemConversion: {
      id: 7,
      minLevel: 441, // TODO: This is a guess based on lowest level of LFR drops
    },
  },
  df2: {
    disableWarforgeLevel: true,
    enableSockets: true,

    // Dragonflight season 2 Revival Catalyst conversion
    itemConversion: {
      id: 6,
      minLevel: 402, // TODO: This is a guess based on lowest level of LFR drops
    },
  },
  df1: {
    disableWarforgeLevel: true,
    enableSockets: true,

    // Dragonflight season 1 Revival Catalyst conversion
    itemConversion: {
      id: 3,
      minLevel: 376,
    },
  },
  // 9.2.7
  sl4: {
    disableWarforgeLevel: true,
    enableSockets: true,
  },
  // 9.2
  sl3: {
    disableWarforgeLevel: true,
    enableSockets: true,
  },
  // 9.1
  sl2: {
    disableWarforgeLevel: true,
    enableSockets: true,
  },
  // 9.0
  sl1: {
    disableWarforgeLevel: true,
  },
  bfa4: {
    disableWarforgeLevel: true,
  },
  bfa3: {
    warforgeMax: 455,
  },
  bfa2: {
    warforgeMax: 425,
  },
};

const OFFSET_INCREMENT = 13;

const OFFSET_LFR = -13;
const OFFSET_NORMAL = 0;
const OFFSET_HEROIC = 13;
const OFFSET_MYTHIC = 26;

// Some instances start their base item level at LFR?
const LFR_BASE_OFFSET_LFR = 0;
const LFR_BASE_OFFSET_NORMAL = 13;
const LFR_BASE_OFFSET_HEROIC = 26;
const LFR_BASE_OFFSET_MYTHIC = 39;

export const INSTANCE_DIFFICULTY_OVERRIDES = [{

  // 11.1 Liberation of Undermine

  instanceId: 1296,
  difficulty: 'raid-lfr',
  itemLevel: '623 - 639',
  levelSelectorSequence: 439, // TWW2 Veteran
}, {
  instanceId: 1296,
  difficulty: 'raid-normal',
  itemLevel: '636 - 652',
  levelSelectorSequence: 440, // TWW2 Champion
}, {
  instanceId: 1296,
  difficulty: 'raid-heroic',
  itemLevel: '649 - 665',
  levelSelectorSequence: 441, // TWW2 Hero
}, {
  instanceId: 1296,
  difficulty: 'raid-mythic',
  itemLevel: '662 - 678',
  levelSelectorSequence: 442, // TWW2 Myth
}, {

  // 11.0.5 Blackrock Depths

  instanceId: 1301,
  difficulty: 'raid-lfr',
  itemLevel: '584 - 593',
  levelSelectorSequence: 331, // TWW1 Veteran
}, {
  instanceId: 1301,
  difficulty: 'raid-normal',
  itemLevel: '597 - 606',
  levelSelectorSequence: 332, // TWW1 Champion
}, {
  instanceId: 1301,
  difficulty: 'raid-heroic',
  itemLevel: '610 - 619',
  levelSelectorSequence: 333, // TWW1 Hero
}, {
  instanceId: 1301,
  difficulty: 'raid-mythic',
  disable: true,
}, {

  // 11.0 Nerub-ar Palance
  // Item based level is 571 which is below LFR

  instanceId: 1273,
  difficulty: 'raid-lfr',
  itemLevel: '584 - 600',
  itemLevelOffset: 1 * OFFSET_INCREMENT,
  levelSelectorSequence: 331, // TWW1 Veteran
}, {
  instanceId: 1273,
  difficulty: 'raid-normal',
  itemLevel: '597 - 613',
  levelSelectorSequence: 332, // TWW1 Champion
}, {
  instanceId: 1273,
  difficulty: 'raid-heroic',
  itemLevel: '610 - 626',
  levelSelectorSequence: 333, // TWW1 Hero
}, {
  instanceId: 1273,
  difficulty: 'raid-mythic',
  itemLevel: '623 - 639',
  levelSelectorSequence: 334, // TWW1 Myth
}, {

  // 10.2 Amirdrassil

  instanceId: 1207,
  difficulty: 'raid-lfr',
  itemLevel: '441 - 450',
  itemLevelOffset: LFR_BASE_OFFSET_LFR,
}, {
  instanceId: 1207,
  difficulty: 'raid-normal',
  itemLevel: '454 - 463',
  itemLevelOffset: LFR_BASE_OFFSET_NORMAL,
}, {
  instanceId: 1207,
  difficulty: 'raid-heroic',
  itemLevel: '467 - 476',
  itemLevelOffset: LFR_BASE_OFFSET_HEROIC,
}, {
  instanceId: 1207,
  difficulty: 'raid-mythic',
  itemLevel: '480 - 489',
  itemLevelOffset: LFR_BASE_OFFSET_MYTHIC,
}, {

  // 10.1 Aberrus, the Shadowed Crucible

  instanceId: 1208,
  difficulty: 'raid-lfr',
  itemLevel: '402 - 418',
  itemLevelOffset: OFFSET_LFR,
}, {
  instanceId: 1208,
  difficulty: 'raid-normal',
  itemLevel: '415 - 431',
  itemLevelOffset: OFFSET_NORMAL,
}, {
  instanceId: 1208,
  difficulty: 'raid-heroic',
  itemLevel: '428 - 444',
  itemLevelOffset: OFFSET_HEROIC,
}, {
  instanceId: 1208,
  difficulty: 'raid-mythic',
  itemLevel: '441 - 457',
  itemLevelOffset: OFFSET_MYTHIC,

}, {

  // 10.0 Vault of the Incarnates

  instanceId: 1200,
  difficulty: 'raid-lfr',
  itemLevel: '376 - 392',
  itemLevelOffset: -13,
}, {
  instanceId: 1200,
  difficulty: 'raid-normal',
  itemLevel: '389 - 405',
  itemLevelOffset: 0,
}, {
  instanceId: 1200,
  difficulty: 'raid-heroic',
  itemLevel: '402 - 418',
  itemLevelOffset: 13,
}, {
  instanceId: 1200,
  difficulty: 'raid-mythic',
  itemLevel: '415 - 430',
  itemLevelOffset: 26,

}];

const ENCOUNTER_OVERRIDES = [{

  // 11.1 Liberation of Undermine

  // Trash drops
  encounterId: FAKE_ENCOUNTER_IDS.boeUndermineTrash,
  encounterSequenceOffset: 0,

}, {

  // 11.1 World Boss (Gobfather)
  encounterId: 2683,
  itemLevelOverride: 642,
  season: 'tww2',

}, {

  // Vexie and the Geargrinders
  encounterId: 2639,
  encounterSequenceOffset: 0,
}, {
  // Cauldron of Carnage
  encounterId: 2640,
  encounterSequenceOffset: 1,
}, {
  // Rik Reverb
  encounterId: 2641,
  encounterSequenceOffset: 1,
}, {
  // Stix Bunkjunker
  encounterId: 2642,
  encounterSequenceOffset: 2,
}, {
  // Sprocketmonger Lockenstock
  encounterId: 2653,
  encounterSequenceOffset: 2,
  encounterVeryRareSequenceOffset: 3,
}, {
  // The One-Armed Bandit
  encounterId: 2644,
  encounterSequenceOffset: 2,
  encounterVeryRareSequenceOffset: 3,
}, {
  // Mug'Zee, Heads of Security
  encounterId: 2645,
  encounterSequenceOffset: 3,
  encounterVeryRareSequenceOffset: 2,
}, {
  // Chrome King Gallywix
  encounterId: 2646,
  encounterSequenceOffset: 3,
  encounterVeryRareSequenceOffset: 2,
}, {

  // 11.0.5 Blackrock Depths

  // Lord Roccor
  encounterId: 2663,
  encounterSequenceOffset: 0,
}, {
  // Bael'Gar
  encounterId: 2664,
  encounterSequenceOffset: 0,
}, {
  // Lord Incendius
  encounterId: 2665,
  encounterSequenceOffset: 1,
}, {
  // Golem Lord Argelmach
  encounterId: 2666,
  encounterSequenceOffset: 1,
}, {
  // The Seven
  encounterId: 2667,
  encounterSequenceOffset: 1,
}, {
  // General Angerforge
  encounterId: 2668,
  encounterSequenceOffset: 2,
}, {
  // Ambassador Flamelash
  encounterId: 2669,
  encounterSequenceOffset: 2,
}, {
  // Emperor Dagran Thaurissan
  encounterId: 2670,
  encounterSequenceOffset: 3,
}, {


  // 11.0 Nerub-ar Palance

  // Trash drops
  encounterId: FAKE_ENCOUNTER_IDS.boeNerubarTrash,
  encounterSequenceOffset: 2,
}, {

  // Ulgrax
  encounterId: 2607,
  encounterSequenceOffset: 0,
}, {
  // Bloodbound Horror
  encounterId: 2611,
  encounterSequenceOffset: 0,
}, {
  // Sikran
  encounterId: 2599,
  encounterSequenceOffset: 1,
  encounterVeryRareSequenceOffset: 4,
}, {
  // Rasha'nan
  encounterId: 2609,
  encounterSequenceOffset: 1,
  encounterVeryRareSequenceOffset: 4,
}, {
  // Broodtwister
  encounterId: 2612,
  encounterSequenceOffset: 2,
}, {
  // Nexus-Princess Ky'veza
  encounterId: 2601,
  encounterSequenceOffset: 2,
  encounterVeryRareSequenceOffset: 3,
}, {
  // The Silken Court
  encounterId: 2608,
  encounterSequenceOffset: 3,
}, {
  // Queen Ansurek
  encounterId: 2602,
  encounterSequenceOffset: 3,
  encounterVeryRareSequenceOffset: 2,
}, {

  // Awakened Raid stuff, wasn't needed when the raid originally came out

  // Vault of the Incarnates

  // Eranog
  encounterId: 2480,
  encounterSequenceOffset: 0,
}, {
  // Terros
  encounterId: 2500,
  encounterSequenceOffset: 0,
}, {
  // The Primal Council
  encounterId: 2486,
  encounterSequenceOffset: 0,
}, {
  // Sennarth
  encounterId: 2482,
  encounterSequenceOffset: 0,
}, {
  // Dathea
  encounterId: 2502,
  encounterSequenceOffset: 2,
}, {
  // Kurog
  encounterId: 2491,
  encounterSequenceOffset: 2,
}, {
  // Broodkeeper Diurna
  encounterId: 2493,
  encounterSequenceOffset: 3,
}, {
  // Raszageth
  encounterId: 2499,
  encounterSequenceOffset: 3,
}, {

  // Aberrus

  // Kazzara
  encounterId: 2522,
}, {
  // Forgotten Experiments
  encounterId: 2530,
  encounterSequenceOffset: 1,
}, {
  // Rashok
  encounterId: 2525,
  encounterSequenceOffset: 1,
}, {
  // Zskarn
  encounterId: 2532,
  encounterSequenceOffset: 2,
}, {
  // Magmorax
  encounterId: 2527,
  encounterSequenceOffset: 2,
}, {
  // Echo of Neltharion
  encounterId: 2523,
  encounterSequenceOffset: 3,
}, {
  // Sarkareth
  encounterId: 2520,
  encounterSequenceOffset: 3,
}, {

  // 10.2 Amirdrassil
  // Back to manually editing item level offsets for each encounter :(

  // Volcoross
  encounterId: 2557,
  encounterSequenceOffset: 1,
}, {
  // Council of Dreams
  encounterId: 2555,
  encounterSequenceOffset: 1,
}, {
  // Larodar, Keeper of the Flame
  encounterId: 2553,
  encounterSequenceOffset: 2,
}, {
  // Nymue, Weaver of the Cycle
  encounterId: 2556,
  encounterSequenceOffset: 2,
  encounterVeryRareSequenceOffset: 1,
}, {
  // Smolderon
  encounterId: 2563,
  encounterSequenceOffset: 2,
}, {
  // Tindral Sageswift
  encounterId: 2565,
  encounterSequenceOffset: 3,
}, {
  // Fyrakk
  encounterId: 2519,
  encounterSequenceOffset: 3,
}, {

  // Dawn of the Infinite hard mode last boss
  /*
  encounterId: 2538,
  difficulty: 'megadungeon-heroic-hard',
  itemLevelOverride: 480,
}, {
  encounterId: 2538,
  difficulty: 'megadungeon-mythic-hard',
  itemLevelOverride: 506,
}, {
*/

  // 10.2 Aurostor, the Hibernator
  encounterId: 2562,
  itemLevelOverride: 460,
  season: 'df3',
}, {
  // Zaqali Elders world boss
  encounterId: 2531,
  itemLevelOverride: 415,
  season: 'df2',

/*
{
  ///////////////////////////////////////////////
  // 9.2.7 FATED RAIDS
  // Also simplify fixed increased ilevel bosses

  // THE JAILER
  encounterId: 2464,
  encounterLevelOffset: 7,
}, {
  // RYGELON
  encounterId: 2467,
  encounterLevelOffset: 7,
}, {
  // LORDS OF DREAD
  encounterId: 2457,
  encounterLevelOffset: 7,
}, {
  // SANCTUM OF DOMINATION
  // KEL'THUZAD
  encounterId: 2440,
  encounterLevelOffset: 7,
}, {
  // SYLVANAS WINDRUNNER
  encounterId: 2441,
  encounterLevelOffset: 7,
}, {
  // CASTLE NATHRIA
  // STONE LEGION GENERALS
  encounterId: 2425,
  encounterLevelOffset: 7,
}, {
  // SIRE DENATHRIUS
  encounterId: 2424,
  encounterLevelOffset: 7,
},
*/
}];

const INSTANCE_ITEM_OVERRIDES = [{
  // Season 4 Raid Vendor items that have higher upgrade tracks

  // Blossom of Amirdrassil
  instanceId: -58,
  itemId: 207171,
  upgradeSet: 382,
}, {
  // Augury of the Primal Flame
  instanceId: -58,
  itemId: 208614,
  upgradeSet: 382,
}, {
  // Fyrakk's Tainted Rageheart
  instanceId: -58,
  itemId: 207174,
  upgradeSet: 382,
}, {
  // Neltharion's Call to Chaos
  instanceId: -58,
  itemId: 204201,
  upgradeSet: 382,
}, {
  // Neltharion's Call to Dominance
  instanceId: -58,
  itemId: 204202,
  upgradeSet: 382,
}, {
  // Neltharion's Call to Suffering
  instanceId: -58,
  itemId: 204211,
  upgradeSet: 382,
}, {
  // Voice of the Silent Star
  instanceId: -58,
  itemId: 204465,
  upgradeSet: 382,
}, {
  // Seal of Filial Duty
  instanceId: -58,
  itemId: 195526,
  upgradeSet: 382,
}, {
  // Neltharax, Enemy of the Sky
  instanceId: -58,
  itemId: 195527,
  upgradeSet: 382,
}, {
  // Nasz'uro, the Unbound Legacy
  instanceId: -58,
  itemId: 204177,
  upgradeSet: 382,
}, {
  // Fyr'alath the Dreamrender
  instanceId: -58,
  itemId: 206448,
  upgradeSet: 382,
}];

const getDifficulty = difficultyId => _.find(DIFFICULTIES, { id: difficultyId });
const getSeason = seasonId => SEASONS[seasonId];

const getOverrides = (instanceId, encounterId, difficultyId, itemId) => {
  const instance = getInstance(instanceId);
  const encounter = getEncounter(encounterId);
  let difficulty = getDifficulty(difficultyId);

  // instance + encounter
  let encounterOverrides = [];
  const allEncounterOverrides = _.filter(
    ENCOUNTER_OVERRIDES,
    {
      encounterId,
    },
  );
  if (difficultyId) {
    encounterOverrides = _.find(
      allEncounterOverrides,
      {
        difficulty: difficultyId,
      },
    );
  } else if (instance) {
    // if difficultyId is null, see if there is a single matching difficulty for the instance type
    // this should catch things like pvp-conquest where there is only 1 option
    const difficulties = _.filter(DIFFICULTIES, { type: instance.type });
    if (difficulties.length === 1) {
      difficulty = _.first(difficulties);
    }
  }
  // didn't find specific difficulty overrides, use a generic one if available
  if (_.size(encounterOverrides) === 0) {
    encounterOverrides = _.find(
      allEncounterOverrides,
      o => !o.difficulty,
    );
  }

  // instance + difficulty
  const instanceDifficultyOverrides = _.find(
    INSTANCE_DIFFICULTY_OVERRIDES,
    {
      instanceId,
      difficulty: difficultyId,
    },
  );

  // general instance overrides
  const instanceOverrides = _.find(
    INSTANCE_OVERRIDES,
    {
      instanceId,
    },
  );

  const itemOverrides = _.find(
    INSTANCE_ITEM_OVERRIDES,
    {
      instanceId,
      itemId,
    },
  );

  let season;
  if (instanceOverrides) {
    season = getSeason(instanceOverrides.season);
  }
  if (encounterOverrides?.season) {
    season = getSeason(encounterOverrides.season);
  }

  const overrides = {};

  _.defaults(
    overrides,
    itemOverrides,
    encounterOverrides,
    instanceDifficultyOverrides,
    instanceOverrides,
    season,
    {
      instance,
      encounter,
      difficulty,
      context: difficulty?.context,
      encounterType: 'boss',
      encounterTypePlural: 'bosses',
      quality: difficulty?.quality ?? 4,

      // add some difficulty options at the top level (but not everything)
      // (Not sure why not everything, need to spend some more time remembering why it wrote it that way)
      itemLevel: difficulty?.itemLevel,
      itemLevelOverride: difficulty?.itemLevelOverride,
      itemLevelOffset: difficulty?.itemLevelOffset,
      gambling: difficulty?.gambling,
      itemFilter: difficulty?.itemFilter,

      levelSelectorSequence: difficulty?.levelSelectorSequence,
    },
  );

  return overrides;
};

const equipSlotLookupMap = {
  finger: 'finger1',
  trinket: 'trinket1',
};

const getEquippedPrismaticGemsToCopy = (equipped, slot) => {
  const equippedSlot = equipSlotLookupMap[slot] || slot;
  const getEquippedGems = checkSlot => {
    return _.map(_.filter(_.split(equipped[checkSlot]?.gem_id, '/')), g => parseInt(g, 10));
  };
  const equippedGemIds = getEquippedGems(equippedSlot);
  let equippedPrismaticGems = _.filter(
    equippedGemIds,
    gemId => {
      // only grab prismatic gems
      // Avoids copying 11.0.7 Citrine gems into normal sockets
      return getGemDetails(gemId)?.socketType === SOCKET_TYPES.PRISMATIC;
    },
  );
  if (_.size(equippedGemIds) && !_.size(equippedPrismaticGems)) {
    if (_.includes(['finger1', 'finger2'], equippedSlot)) {
      const otherSlot = equippedSlot === 'finger1' ? 'finger2' : 'finger1';
      equippedPrismaticGems = _.filter(
        getEquippedGems(otherSlot),
        g => getGemDetails(g)?.socketType === SOCKET_TYPES.PRISMATIC,
      );
    }
  }
  return _.filter(equippedPrismaticGems);
};

const getItems = ({
  instance,
  encounter,
  instances, // legacy
  encounters, // legacy
  difficulty,
  warforgeLevel,
  upgradeLevel,
  gem,
  classId,
  specId,
  lootSpecId,
  faction,
  // Use static data by default, but allow overrides.
  // This lets reports save a snapshot of the item/instance list from sim time so that
  // things don't break as Droptimizer updates are made.
  // Also means Droptimizer data doesn't need to be loaded on a report page.
  itemLibrary = EncounterItems,
  instanceLibrary = Instances,
  equipped,
  craftedStats,
  craftedGem,
  offSpecItems,
  includeConversions,
}) => {
  if (!itemLibrary && !loaded) {
    // eslint-disable-next-line
    console.warn('Droptimizer is not ready, must call loadStaticData() with proper data');
    return [];
  }

  const selectedInstanceId = instance || _.first(instances);
  const selectedEncounterId = instance ? encounter : _.first(encounters);

  // verify instance, encounter, and difficulty exist
  if (selectedInstanceId && !getInstance(selectedInstanceId)) {
    throw new Error('instance_not_found');
  }

  const overrides = getOverrides(selectedInstanceId, selectedEncounterId, difficulty);

  const baseItems = _.map(
    _.filter(itemLibrary, item => {
      if (item.azeriteItem) {
        return false;
      }

      if (!_.includes(_.map(item.sources, 'instanceId'), selectedInstanceId)) {
        return false;
      }

      if (offSpecItems) {
        if (!isItemUsable(item, classId, specId)) {
          return false;
        }
      } else if (!doesItemDrop(item, classId, specId)) {
        return false;
      }

      if (item.faction && item.faction !== faction) {
        return false;
      }

      const encounterId = _.find(item.sources, { instanceId: instance }).encounterId;

      const itemOverrides = getOverrides(selectedInstanceId, selectedEncounterId || encounterId, difficulty, item.id);
      if (itemOverrides.disabled) {
        return false;
      }

      const checkEncounterId = selectedEncounterId && selectedEncounterId !== -1;
      if (checkEncounterId && selectedEncounterId !== encounterId) {
        return false;
      }

      if (itemOverrides.itemFilter) {
        return itemOverrides.itemFilter(item);
      }

      return true;
    }),
    item => (
      // strip any sockets that may have come from the itemLibrary
      removeAllPrismaticSockets({
        ...item,
        baseItemLevel: item.itemLevel,

        // strip enchants that may have come in from the itemLibrary
        enchant_id: null,
      })
    ),
  );

  // expand tokens into possible drops
  const expandedItems = [];
  _.each(baseItems, item => {
    if (item.contains) {
      _.each(item.contains, itemId => {
        const it = _.find(itemLibrary, { id: itemId });
        if (it) {
          if (doesItemDrop(it, classId, lootSpecId || specId)) {
            let newItem = {
              ...it,
              // copy some data from the token
              sources: item.sources,
              sourceItem: item,
              contextLevels: item.contextLevels,
              fromToken: true,
            };

            // * Make sure we use the token's base item level
            // possible that we might need to do some of the base item level shuffling done below
            // Update: we did
            // TODO: Rewrite the target item level handling in all of this using some of the
            // newer item level management code in shared/items.js
            newItem = removeItemLevelBonuses(newItem);
            // capture actual item's base item level
            newItem.baseItemLevel = it.itemLevel;
            newItem.itemLevel = item.baseItemLevel || item.itemLevel;

            expandedItems.push(newItem);
          }
        }
      });
    } else {
      // get all variations from the bonus variation functions, the base one will be ignored
      // this helps line up actors in the report eventually
      const bonusVariations = getBonusVariations(item, false);

      if (bonusVariations) {
        expandedItems.push(...bonusVariations);
      } else {
        expandedItems.push({ ...item });
      }

      if (overrides.expandCraftedStats) {
        if (hasCraftedStats(item)) {
          _.each(CRAFTED_STAT_COMBOS, craftedStatCombo => {
            if (craftedStatCombo.stats.length === numCraftedStats(item)) {
              expandedItems.push({
                ...item,
                statCombo: craftedStatCombo,
                crafted_stats: craftedStatCombo.id,
              });
            }
          });
        }
      }

      // check for overrides for the encounter for this item
      // this ensures new world bosses use the right item conversion and old bosses keep using the old conversion
      const encounterId = _.find(item.sources, { instanceId: instance }).encounterId;

      const itemOverrides = getOverrides(selectedInstanceId, selectedEncounterId || encounterId, difficulty, item.id);
      const conversion = itemOverrides.itemConversion || overrides.itemConversion;
      if (conversion && includeConversions) {
        const possibleConversions = getPossibleItemConversions(item, specId, conversion.id);
        _.each(possibleConversions, convertedItem => {
          let newItem = {
            ...convertedItem,
            sources: item.sources,
            itemConversion: conversion.id,
            // include a reference to the original item. May need it to handle sources configured with an offset later
            sourceItem: item,
          };

          // * Make sure we use the token's base item level
          // we want this item back at its base item level in case we deal with offsets later
          // yeah, it's pretty hacky
          newItem = removeItemLevelBonuses(newItem);
          // capture actual item's base item level
          newItem.baseItemLevel = convertedItem.baseItemLevel || convertedItem.itemLevel;
          newItem.itemLevel = item.baseItemLevel || item.itemLevel;

          newItem = addTag(newItem, 'catalyst');

          expandedItems.push(newItem);
        });
      }
    }
  });

  const items = _.sortBy(
    _.filter(_.map(
      expandedItems,
      item => {
        const isDebugItem = DEVMODE && DEBUG_ITEM_ID === item.id;

        const slot = slotMap[item.inventoryType];

        const source = _.find(item.sources, { instanceId: selectedInstanceId }) || _.first(item.sources);

        const instanceId = selectedInstanceId || source.instanceId;
        const encounterId = selectedEncounterId || source.encounterId;

        const itemOverrides = getOverrides(instanceId, encounterId, difficulty, item.id);

        // Pull any "innate" item bonuses related to sockets or item level
        const innateBonusIds = getBonusIds(item);
        let baseBonusIds = _.filter(
          innateBonusIds,
          bonusId => {
            return isItemLevelBonus(bonusId)
              || isBaseLevelBonus(bonusId)
              || isSocketBonus(bonusId)
              || isItemVariationBonus(bonusId);
          },
        );

        let gems = [];
        let addSocket = false;
        if (isUngatedSocketSlot(slot)) {
          const equippedSlot = equipSlotLookupMap[slot] || slot;
          const equippedPrismaticGems = getEquippedPrismaticGemsToCopy(equipped, slot);
          if (_.size(equippedPrismaticGems)) {
            // Dragonflight!
            // Copy sockets/gems from equipped neck if there are gems in it.
            // Neck sockets can always be matched because of the JC items that can be bough on the AH.
            // Only do this if there are gems. If there are no gems, the craftedGem section below will run
            const equippedItem = equipped[equippedSlot];
            const equippedSocketBonusId = getItemSocketBonusId(equippedItem);
            const itemSocketBonusId = getItemSocketBonusId(item);
            if (equippedSocketBonusId) {
              // remove any different socket bonus IDs
              if (itemSocketBonusId && itemSocketBonusId !== equippedSocketBonusId) {
                baseBonusIds = _.without(baseBonusIds, itemSocketBonusId);
              }
              addSocket = equippedSocketBonusId;
            }
            gems = equippedPrismaticGems;
          }
        } else if (itemOverrides.enableSockets && gem && !warforgeLevel && canAddSocket(item, SOCKET_TYPES.PRISMATIC)) {
          const numPossibleSockets = numSockets(item, SOCKET_TYPES.PRISMATIC);
          // if adding a socket
          addSocket = getBonusIdForSocketNum(numPossibleSockets);
          for (let i = 0; i < numPossibleSockets; i += 1) {
            gems.push(gem);
          }
        } else if (craftedGem) {
          // if handling a crafted item with a default socket
          _.each(item.socketInfo?.sockets, socket => {
            if (socket.type === 'PRISMATIC') {
              gems.push(craftedGem);
            }
          });
        }

        if (!itemOverrides.disableDifficulties) {
          baseBonusIds = _.union(baseBonusIds, _.get(_.find(DIFFICULTIES, { id: difficulty }), 'bonusIds', []));
        }

        const instanceBonusIds = _.get(itemOverrides, 'bonusIds', []);

        const isWeapon = _.includes(['main_hand', 'off_hand'], slot);
        let bonusIds = _.flatten(
          _.map(
            _.union(baseBonusIds, instanceBonusIds),
            bonusId => {
              // adjust level bonus ID to handle warforge level offset
              const isLevelBonus = _.isNumber(GameData.BonusLevels[String(bonusId)]);
              if (warforgeLevel && isLevelBonus) {
                let amount = isWeapon ? _.min([warforgeLevel, 10]) : warforgeLevel;

                const newItemLevel = itemOverrides.itemLevel + amount;
                if (newItemLevel > itemOverrides.warforgeMax) {
                  amount -= newItemLevel - itemOverrides.warforgeMax;
                }

                let warforgeBonusId = null;
                if (amount > 10) {
                  warforgeBonusId = TITANFORGED_BONUS_ID;
                } else if (amount > 0) {
                  warforgeBonusId = WARFORGED_BONUS_ID;
                }
                return _.filter([bonusId + amount, warforgeBonusId]);
              }
              return bonusId;
            },
          ),
        );

        // only keep last level offset bonus ID
        bonusIds = _.filter(_.union(
          // get all non-level-offset bonus IDs
          _.reject(bonusIds, id => GameData.BonusLevels[String(id)]),
          [_.last(_.filter(bonusIds, id => GameData.BonusLevels[String(id)]))],
        ));

        const levelOffset = _.last(
          _.filter(
            _.map(bonusIds, id => GameData.BonusLevels[String(id)]),
          ),
        ) || 0;

        if (addSocket) {
          bonusIds.push(addSocket);
        }

        let itemLevel = (item.itemLevel) + levelOffset;
        const baseItemLevel = item.baseItemLevel || itemLevel;

        const itemUpdateReasons = [];
        const updateItemLevelBonuses = (newLevel, reason) => {
          // find appropriate bonus ID for the full item level offset
          const fullOffset = newLevel - baseItemLevel;
          const levelBonusId = getItemBonusForDelta(fullOffset);

          // remove any existing level adjust bonus IDs
          bonusIds = _.reject(bonusIds, isItemLevelBonus);

          // add the right bonus ID
          bonusIds.push(levelBonusId);

          itemLevel = newLevel;

          itemUpdateReasons.push({
            newLevel,
            reason,
            levelBonusId,
            baseItemLevel,
          });
        };

        let upgradeBonusId;

        const { levelSelectorSequence } = itemOverrides;
        if (item.contextLevels && item.contextLevels[itemOverrides.context]) {
          updateItemLevelBonuses(item.contextLevels[itemOverrides.context], 'item has explicit context level');
        } else if (levelSelectorSequence) {
          let sequenceIndex = 0;

          if (itemOverrides.encounterSequenceOffset) {
            sequenceIndex += itemOverrides.encounterSequenceOffset;
          }

          if (source.veryRare) {
            sequenceIndex += itemOverrides.encounterVeryRareSequenceOffset ?? DEFAULT_VERY_RARE_SEQUENCE_OFFSET;
          }

          const upgradeSetLevel = getUpgradeSetLevel(levelSelectorSequence, sequenceIndex + 1)
            || getUpgradeSetLevel(levelSelectorSequence, sequenceIndex);

          if (upgradeSetLevel) {
            const sequenceLevel = upgradeSetLevel.itemLevel;

            // Explicitly set the upgrade bonus ID here. The result is that this will set an exact upgrade level
            // instead of deferring that decision later which would choose the highest track.
            // This is currently due to 11.0 Nerub-ar Palace Very Rare gear. Instead of pushing a 6/8 Champion to 2/2
            // Hero, it leaves it at 6/8 Champion in the Encounter Journal (at least in the beta right now).
            // Possible Blizz will change this when season 1 starts
            if (itemOverrides.levelSelectorSetUpgradeTrack) {
              upgradeBonusId = upgradeSetLevel.bonusId;
            }

            updateItemLevelBonuses(sequenceLevel, 'levelSelectorSequence');
            /*
          } else {
            console.log('No upgradeSetLevel', item.name, levelSelectorSequence, sequenceIndex);
            */
          }
        } else {
          const { itemLevelOverride } = itemOverrides;
          if (itemLevelOverride) {
            let desiredItemLevel = itemLevelOverride;
            if (_.isFunction(itemLevelOverride)) {
              desiredItemLevel = itemLevelOverride(item);
            }
            updateItemLevelBonuses(desiredItemLevel, 'desiredItemLevel');
          }

          let encounterLevelOffset = itemOverrides.encounterLevelOffset || 0;

          if (source.veryRare) {
            encounterLevelOffset += (itemOverrides.veryRareLevelOffset || 6);
          }

          // NOTE: Important to differentiate between 0 and null/undefined. 0 offset is used for normal raids
          // since that matches the base level of the item
          if (encounterLevelOffset !== undefined) {
            updateItemLevelBonuses(itemLevel + encounterLevelOffset, 'encounterLevelOffset');
          }
          if (itemOverrides.itemLevelOffset !== undefined) {
            updateItemLevelBonuses(itemLevel + itemOverrides.itemLevelOffset, 'itemLevelOffset');
          }

          // Rare case of a single item with a different item level without something like the
          // very rare flag. Neck from the 10.2 world boss has a neck at a higher item level
          const itemOffset = itemOverrides.items?.[item.id]?.offset;

          if (itemOffset) {
            updateItemLevelBonuses(itemLevel + itemOffset, 'itemOffset');
          }
        }

        // in the case of catalyst conversion and a droptimizer item level offset, we need to make sure
        // the transformed item ends up as the same item level as the original. If these two items have
        // different base levels, we need to handle that offset in addition to the droptimizer offset
        let sourceItemOffset = 0;
        if (item.sourceItem) {
          if (item.sourceItem.itemLevel > itemLevel) {
            sourceItemOffset = item.sourceItem.itemLevel - itemLevel;
          }
          if (item.sourceItem.slotItemLevelOffsets) {
            sourceItemOffset += item.sourceItem.slotItemLevelOffsets[slot] || 0;
          }
          if (levelSelectorSequence && item.sourceItem.slotItemSequenceOffsets) {
            // find sequence for the source item
            const sourceIndex = getIndexForSequenceLevel(levelSelectorSequence, itemLevel);
            const newSequenceOffset = sourceIndex + item.sourceItem.slotItemSequenceOffsets[slot] || 0;
            const newSequenceLevel = getUpgradeSetLevel(levelSelectorSequence, newSequenceOffset + 1)?.itemLevel;

            updateItemLevelBonuses(newSequenceLevel, 'source item item sequence offset');
          }
        }

        if (sourceItemOffset) {
          updateItemLevelBonuses(itemLevel + sourceItemOffset, 'sourceItemOffset');
        }

        const { itemLevelMin } = itemOverrides;
        if (itemOverrides.itemLevelMin) {
          if (itemLevel < itemLevelMin) {
            updateItemLevelBonuses(itemLevelMin, 'itemLevelMin');
          }
        }

        // copy enchants/non-prismatic gems from equipped gear
        const equippedEnhancements = {
          enchant_id: null,
          tooltipParams: {
            enchant: null,
          },
        };
        let couldUpgrade;
        if (equipped) {
          // find the right item to compare against
          const camelSlotName = _.camelCase(slot);
          const equippedSlotName = equipSlotLookupMap[camelSlotName] || camelSlotName;
          const equippedItem = equipped[equippedSlotName];

          if (equippedItem) {
            // check for an existing enchant
            const equippedEnchant = equippedItem.tooltipParams?.enchant || 0;
            equippedEnhancements.enchant_id = equippedEnchant;
            equippedEnhancements.tooltipParams.enchant = equippedEnchant;

            _.each(equippedItem?.socketInfo, (socketInfo, socketType) => {
              if (!isUngatedSocketSlot(slot)) {
                const socketIsPrismatic = socketType === SOCKET_TYPES.PRISMATIC;
                // funky format of sockets when parsed client side
                const potentialSocketInfo = _.find(item.socketInfo?.sockets, { type: socketType });
                if (socketIsPrismatic) {
                  if (addSocket && socketInfo.gemIds.length > 0) {
                    gems.pop();
                    gems.push(socketInfo.gemIds[0]);
                  }
                } else if (!socketIsPrismatic && potentialSocketInfo) {
                  // copy equipped gem into potential
                  // make sure we keep the prismatic gem from the UI option if present
                  gems.push(...socketInfo.gemIds);
                }
              }
            });
          }
        }

        const difficultyInfo = _.find(DIFFICULTIES, { id: difficulty });

        let itemCraftedStats;
        // only add crafted_stats if the item has the open stat slot
        if (hasCraftedStats(item)) {
          if (overrides.expandCraftedStats) {
            // if this source expands stats, it's already set so save it for later
            itemCraftedStats = item.crafted_stats;
          } else {
            itemCraftedStats = craftedStats;
          }
        }

        if (item.itemConversion && itemLevel < item.itemConversion.minLevel) {
          return null;
        }


        const dropLevel = itemLevel;

        if (upgradeLevel) {
          const upgradeInfo = getUpgradeInfoForBonus(upgradeLevel);

          if (upgradeInfo) {
            let currentUpgradeBonus;
            if (itemOverrides.levelSelectorSetUpgradeTrack) {
              // 11.0 - if we're sticking to the upgrade, look up based on that sequence
              currentUpgradeBonus = getUpgradeBonusForLevel(levelSelectorSequence, dropLevel);
            } else {
              // handle case where item's max upgrade is lower than selected
              const season = _.find(GameData.Seasons, { shortName: itemOverrides.season || overrides.season });
              currentUpgradeBonus = getSeasonBonusForLevel(season.id, dropLevel);
            }
            if (itemOverrides.upgradeSet) {
              // handle droptimizer upgradeSet override
              currentUpgradeBonus = getUpgradeBonusForLevel(itemOverrides.upgradeSet, dropLevel);
            }
            // make sure there is a bonus for this level. Might be a world boss from an older patch
            if (currentUpgradeBonus) {
              const upgradeSet = getBonusUpgradeSet(currentUpgradeBonus);
              const maxUpgradeEntry = _.maxBy(upgradeSet, 'level');
              if (upgradeInfo.itemLevel > dropLevel) {
                if (upgradeInfo.itemLevel > maxUpgradeEntry.itemLevel) {
                  // selected upgrade level is higher than the item's max upgrade, use the item's max upgrade
                  updateItemLevelBonuses(maxUpgradeEntry.itemLevel, 'upgradeLevel - item max');
                  upgradeBonusId = maxUpgradeEntry.bonusId;
                } else {
                  // use the selected upgrade level
                  updateItemLevelBonuses(upgradeInfo.itemLevel, 'upgradeLevel - selected upgrade');
                  // but find the right bonus ID for this item.
                  const targetUpgradeEntry = _.find(upgradeSet, { itemLevel: upgradeInfo.itemLevel });
                  upgradeBonusId = targetUpgradeEntry.bonusId;
                }
              }
            }
          }
        }

        let upgradeInfo;
        if (overrides.season || itemOverrides.season) {
          const season = _.find(GameData.Seasons, { shortName: itemOverrides.season || overrides.season });
          if (season) {
            if (!upgradeBonusId) {
              if (itemOverrides.upgradeSet) {
                // handle droptimizer upgradeSet override
                upgradeBonusId = getUpgradeBonusForLevel(itemOverrides.upgradeSet, itemLevel);
              } else if (overrides.season) {
                upgradeBonusId = getSeasonBonusForLevel(season.id, itemLevel);
                if (!upgradeBonusId) {
                  // HACK: check +1 level
                  // The manually applied encounter item offsets don't work in all cases. Some of the upgrade track
                  // levels increment by 4 instead of 3 so the offsets work for one difficulty but don't quite work at
                  // higher levels.  Long term solution is rewrite some of Droptimizer to fully figure out how Encounter
                  // Journal handles these discrepencies natively and skip all the manual encounter data tweaking
                  upgradeBonusId = getSeasonBonusForLevel(season.id, itemLevel + 1);
                  if (upgradeBonusId) {
                    updateItemLevelBonuses(itemLevel + 1, 'season upgrade hack');
                  } else {
                    // eslint-disable-next-line no-console
                  }
                }
              }
            }
          }
          if (upgradeBonusId) {
            bonusIds.push(upgradeBonusId);

            upgradeInfo = getUpgradeInfoForBonus(upgradeBonusId);
          }
        }

        if (equipped) {
          const searchSlots = {
            finger: ['finger1', 'finger2'],
            trinket: ['trinket1', 'trinket2'],
            main_hand: ['mainHand', 'offHand'],
            off_hand: ['offHand', 'mainHand'],
          };
          const search = searchSlots[slot] || [slot];
          _.each(search, searchSlot => {
            const equippedItem = equipped[searchSlot];
            // now we check if this item is already equipped and could be upgraded
            if (equippedItem?.id === item.id && equippedItem.upgrade && equippedItem.itemLevel < itemLevel) {
              const maxUpgradeInfo = getMaxUpgradeInfo(equippedItem.upgrade.bonusId);
              if (maxUpgradeInfo) {
                if (maxUpgradeInfo.itemLevel >= itemLevel) {
                  couldUpgrade = true;
                }
              }
            }
          });
        }

        const newItem = {
          ...item,
          ...equippedEnhancements,
          quality: itemOverrides.quality ?? 4, // default to epic
          itemLevel,
          dropLevel,
          bonus_id: _.compact(_.union(bonusIds)).join('/'),
          bonusLists: _.compact(_.union(bonusIds)),
          gem_id: _.join(gems, '/'),
          crafted_stats: itemCraftedStats,
          crafting_quality: difficultyInfo?.craftingQuality,
          instanceId,
          encounterId,
          difficulty,
          instance: itemOverrides.instance,
          encounter: itemOverrides.encounter,
          sources: _.filter(item.sources, s => {
            const sourceEncounters = _.flatten(_.map(instanceLibrary, 'encounters'));
            const sourceEncounter = _.find(sourceEncounters, { id: s.encounterId });
            if (!sourceEncounter) {
              return false;
            }
            if (sourceEncounter.faction && sourceEncounter.faction !== faction) {
              return false;
            }
            return true;
          }),
          overrides: itemOverrides,
          offSpecItem: !doesItemDrop(item, classId, specId),
          couldUpgrade,
          upgrade: upgradeInfo,
        };

        newItem.socketInfo = itemSocketInfo(newItem);

        if (isDebugItem) {
          /* eslint-disable no-console */
          console.group(item.name);
          console.log(newItem);
          console.log(itemUpdateReasons);
          console.groupEnd(item.name);
          /* eslint-enable no-console */
        }

        // do some last second checks to try to filter out items that can't actually be crafted at the selected
        // level
        if (item.profession) {
          if (item.baseItemLevel > newItem.itemLevel) {
            return false;
          }
        }

        return newItem;
      },
    )),
    ['itemLevel', 'inventoryType'],
  );

  const dedupeItems = _.uniqBy(
    items,
    i => {
      const instanceId = i.instanceId;
      let encounterId = i.encounterId;
      // Hacky - this handles All Dungeons but will end up with duplicate profilesets with the same name
      // this is kind of preferable since the same item won't sim multiple times but it does result
      // in hacky simc input
      if (instanceId === -1) {
        encounterId = _.find(i.sources, { instanceId })?.encounterId;
      }
      return _.join([
        i.id,
        instanceId,
        encounterId,
        i.itemLevel,
        i.bonusVariationName,
        i.crafted_stats,
      ], '-');
    },
  );

  return dedupeItems;
};

// This is probably OK to naively memoize. Finite number of variations though it may be large
const getVariations = ({
  instance,
  encounter,
  instances,
  encounters,
  difficulty,
  warforgeLevel,
  upgradeLevel,
  gem,
  classId,
  specId,
  lootSpecId,
  faction,
  equipped,
  mainHandAlternateWeapon,
  offHandAlternateWeapon,
  itemLibrary,
  instanceLibrary,
  craftedStats,
  craftedGem,
  offSpecItems,
  includeConversions,
}) => {
  if (!itemLibrary && !loaded) {
    // eslint-disable-next-line
    console.warn('Droptimizer is not ready, must call loadStaticData() with proper data');
    return [];
  }

  const items = getItems({
    instance,
    encounter,
    instances,
    encounters,
    difficulty,
    warforgeLevel,
    upgradeLevel,
    gem,
    classId,
    specId,
    lootSpecId,
    faction,
    itemLibrary,
    instanceLibrary,
    equipped,
    craftedStats,
    craftedGem,
    offSpecItems,
    includeConversions,
  });

  if (!equipped || _.size(equipped) === 0) {
    return null;
  }

  const specInfo = getSpecInfo(classId, specId);

  const variations = [];

  const equippedMainHands = getWeaponHands(equipped.mainHand);

  const hasAlternateWeapons = mainHandAlternateWeapon && offHandAlternateWeapon;

  // generate all variations for the raw item list
  _.each(items, origItem => {
    const item = { ...origItem };

    // eslint-disable-next-line no-unused-vars
    const isDebugItem = DEVMODE && DEBUG_ITEM_ID === item.id;

    // weapon permutations
    const hands = getWeaponHands(item);

    // figure out slots
    const slots = [];
    const rawSlot = slotMap[item.inventoryType];
    if (_.includes(['finger', 'trinket'], rawSlot)) {
      const doubleSlots = [1, 2];
      _.each(doubleSlots, slotNum => {
        const testSlot = `${rawSlot}${slotNum}`;
        const otherSlotNum = _.first(_.xor(doubleSlots, [slotNum]));
        const otherSlot = `${rawSlot}${otherSlotNum}`;
        const otherItem = equipped[otherSlot];
        const replaceItem = equipped[testSlot];

        // generalize for item categories / item limits
        if (!item.uniqueEquipped || otherItem?.id !== item?.id) {
          slots.push({ slot: testSlot });
        }
        // if our new item and the item we're replacing are both On Use, make a new variation
        // with the replaced trinket in the other slot to test double On Use trinket order
        if (item?.onUseTrinket && replaceItem?.onUseTrinket) {
          // do the unique equipped check again for this variation
          if (!item.uniqueEquipped || item?.id !== replaceItem?.id) {
            slots.push({
              slot: testSlot,
              alternate: {
                ...replaceItem,
              },
              alternateSlot: otherSlot,
              variationName: 'swap_doubleonuse',
            });
          }
        }
      });
    } else if (rawSlot === 'main_hand') {
      // 1. Handle dual wield scenarios
      //    a. Caveat for specs that use 2h or dual-wield 1h - currently just match what's in their hands.
      //      Fury always want to match double 1h or double 2h because of Single Minded Fury. mixing 2h and 1h
      //      is unlikely to every be optimal. Droptimizer only swaps in one weapon at a time so it won't
      //      ever be testing changing 2x2h to 2x1h
      const dualWield1h = equippedMainHands === '1h' && hands === '1h' && specInfo.dualWield;
      const dualWield2h = equippedMainHands === '2h' && hands === '2h' && specInfo.dualWield2h;
      if (dualWield1h || dualWield2h) {
        const testSlots = ['main_hand', 'off_hand'];
        _.each(testSlots, testSlot => {
          const otherSlot = _.camelCase(_.first(_.xor(testSlots, [testSlot])));
          if (!item.uniqueEquipped || item.id !== equipped[otherSlot]?.id) {
            slots.push({ slot: testSlot });
          }
        });

        if (!item.uniqueEquipped || item.id !== equipped.mainHand?.id) {
          const enchant = _.get(equipped, 'offHand.tooltipParams.enchant');
          slots.push({
            slot: 'main_hand',
            alternate: {
              ...equipped.mainHand,
              // replace with off hand enchant
              enchant_id: enchant,
              tooltipParams: {
                enchant,
              },
            },
            alternateSlot: 'off_hand',
            variationName: 'swap_mh',
          });
        }
      } else if (equippedMainHands === '2h' && (hands === '1h' || hands === 'mh') && !specInfo.dualWield2h) {
        // Some specs can handle 2h staff or MH/OH if alternates are set up from /simc input
        // (All casters and a few melee specs)
        // Fury warriors are not handled here
        if (offHandAlternateWeapon) {
          slots.push({
            slot: rawSlot,
            alternate: {
              ...offHandAlternateWeapon,
              ilevel: offHandAlternateWeapon.itemLevel,
              bonus_id: _.join(offHandAlternateWeapon.bonusLists, '/'),
            },
            alternateSlot: 'off_hand',
          });
          // if spec can dual wield, also try this item in the off hand
          if (specInfo.dualWield && mainHandAlternateWeapon) {
            slots.push({
              slot: 'off_hand',
              alternate: {
                ...mainHandAlternateWeapon,
                ilevel: mainHandAlternateWeapon.itemLevel,
                bonus_id: _.join(mainHandAlternateWeapon.bonusLists, '/'),
              },
              alternateSlot: 'main_hand',
            });
          }
        }
      } else if (!specInfo.dualWield2h) {
        // for everyone but fury warriors, just add the item
        // all valid fury variations should be handled above, this avoids mixing 2h and 1h weapons in the same set
        slots.push({ slot: rawSlot });
      }
    } else if (rawSlot === 'off_hand') {
      if (equippedMainHands !== '2h') {
        slots.push({ slot: rawSlot });
      } else if (equippedMainHands === '2h' && hasAlternateWeapons && mainHandAlternateWeapon) {
        slots.push({
          slot: rawSlot,
          alternate: {
            ...mainHandAlternateWeapon,
            ilevel: mainHandAlternateWeapon.itemLevel,
            enchant_id: _.get(equipped, 'mainHand.tooltipParams.enchant'),
            bonus_id: _.join(mainHandAlternateWeapon.bonusLists, '/'),
          },
          alternateSlot: 'main_hand',
        });
      }
    } else {
      slots.push({ slot: rawSlot });
    }

    const equippedItemLimits = {};
    _.each(equipped, it => {
      if (it.itemLimit) {
        if (!equippedItemLimits[it.itemLimit.category]) {
          equippedItemLimits[it.itemLimit.category] = {
            max: it.itemLimit.quantity,
            count: 0,
          };
        }
        const obj = equippedItemLimits[it.itemLimit.category];
        obj.count += 1;
        if (obj.count === obj.max) {
          obj.full = true;
        }
      }
    });

    _.each(slots, ({ slot, alternate, alternateSlot, variationName }) => {
      let slotItem = { ...item };
      const equippedItem = equipped[_.camelCase(slot)];
      const altSlotEquippedItem = equipped[_.camelCase(alternateSlot)];

      let invalidReason;
      const sameItemIdEquipped = _.get(item, 'id') === _.get(equippedItem, 'id');
      if (sameItemIdEquipped) {
        // if we're upgrading items, make sure we're using the new item level of the equipped item
        if (equippedItem.itemLevel >= item.itemLevel) {
          // equipped gem, potential gem = skip potential
          // equipped no gem, potential no gem = skip potential
          // equipped gem, potential no gem = skip potential
          // equipped no gem, potential gem = sim potential
          const equippedHasGem = Boolean(_.get(equippedItem, 'socketInfo.PRISMATIC.filled'));
          const potentialHasGem = Boolean(_.get(item, 'socketInfo.PRISMATIC.filled'));
          const droptimizerGemBetter = !equippedHasGem && potentialHasGem;

          let equippedCraftedStats = getCraftedStats(equippedItem);
          let droptimizerCraftedStats = getCraftedStats(item);

          if (!craftedStatsOrderMatters(item)) {
            // if Haste/Mastery is the same as Mastery/Haste because crafted stat allocs are the same
            equippedCraftedStats = _.sortBy(equippedCraftedStats);
            droptimizerCraftedStats = _.sortBy(droptimizerCraftedStats);
          }
          const craftedStatsSame = _.join(equippedCraftedStats, '/') === _.join(droptimizerCraftedStats, '/');

          if (!droptimizerGemBetter && craftedStatsSame) {
            if (equippedItem.upgradeOrig) {
              invalidReason = 'equippedCanUpgrade';
            } else {
              invalidReason = 'equippedSameOrBetter';
            }
          }
        }
      }

      if (variationName === 'swap_doubleonuse') {
        // only need to check ID and item level since this is just trinkets
        const mainSwapped = item.id === altSlotEquippedItem?.id && item.itemLevel === altSlotEquippedItem?.itemLevel;
        const altSwapped = alternate.id === equippedItem.id && alternate.itemLevel === equippedItem.itemLevel;
        if (mainSwapped && altSwapped) {
          invalidReason = 'equippedSameOrBetter';
        }
      }

      // run enchant overrides again to make sure off hands get proper enchant on them
      let enchantCopyItem = equippedItem;
      if (equippedMainHands === '2h' && hands === '1h' && hasAlternateWeapons) {
        // copy the right enchant when a monk has 2h equipped but is testing 1h weapons
        enchantCopyItem = slot === 'main_hand' ? mainHandAlternateWeapon : offHandAlternateWeapon;
      }
      const enchant = _.get(enchantCopyItem, 'tooltipParams.enchant') || 0;
      _.set(slotItem, 'enchant_id', enchant);
      _.set(slotItem, 'tooltipParams.enchant', enchant);

      // copy gems (including the case of no gems) from equipped item if ungated
      if (isUngatedSocketSlot(slot)) {
        const copyGems = getEquippedPrismaticGemsToCopy(equipped, slot);
        // make sure we are copying prismatic gems
        _.set(slotItem, 'gem_id', _.join(copyGems, '/'));
        // make sure we set the bonus ID for num sockets as well
        slotItem = setItemNumSockets(slotItem, _.size(copyGems));
      }

      // make sure we don't end up with two unique gems in rings
      if (_.includes(['finger1', 'finger2'], slot)) {
        const otherSlot = slot === 'finger1' ? 'finger2' : 'finger1';

        const otherHasUnique = equipped[otherSlot]?.socketInfo[SOCKET_TYPES.PRISMATIC]?.hasUnique;
        const itemHasUnique = slotItem.socketInfo[SOCKET_TYPES.PRISMATIC]?.hasUnique;

        if (itemHasUnique && otherHasUnique) {
          // replace unique gem with the normal choice
          const uniqueGem = _.find(slotItem.socketInfo[SOCKET_TYPES.PRISMATIC].gems, g => g.unique).itemId;
          slotItem.gem_id = slotItem.gem_id.replace(uniqueGem, gem || '');
          slotItem.socketInfo = itemSocketInfo(slotItem);
        }
      }

      // check item limit constraints
      const limitCategory = item.itemLimit?.category;
      // make sure we check the correct item for item limits if we're swapping things around
      const itemToCheck = alternateSlot ? altSlotEquippedItem : equippedItem;
      const equippedIsCategory = itemToCheck?.itemLimit?.category === limitCategory;
      // if the equipped slot is this category, we can swap it in without violating the constraint
      if (limitCategory && !equippedIsCategory && equippedItemLimits[limitCategory]?.full) {
        invalidReason = 'itemLimitCategory';
      }

      const nameParts = [
        slotItem.instanceId,
        slotItem.encounterId,
        slotItem.difficulty,
        slotItem.id,
        slotItem.itemLevel,
        enchant,
        slot,
        slotItem.bonusVariationName, // bonus ID  variation
        slotItem.statCombo?.value,
        variationName,
      ];

      const baseVariation = {
        id: _.join(nameParts, '/'),
        slot,
        item: slotItem,
        invalidReason,
      };

      variations.push({
        ...baseVariation,
        alternate: _.clone(alternate),
        alternateSlot,
        variationName,
      });
    });
  });

  return variations;
};

const shouldCalcRolls = ({ /*gem,*/ warforgeLevel }) => {
  /*
  if (gem) {
    return false;
  }
  */
  if (warforgeLevel > 0) {
    return false;
  }
  return true;
};

export default {
  ready: () => loaded,
  loadStaticData,

  getItems,
  getVariations,
  getInstance,
  getEncounter,
  getOverrides,
  getDifficulty,

  shouldCalcRolls,

  getEncounters: () => Encounters,
  getInstances: () => Instances,
  getEncounterItems: () => EncounterItems,
};
